import 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardHeader,
  Divider,
  CardContent,
  Button,
  Box,
  CardActions,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormGroup,
  CircularProgress
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';
import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import axios from 'axios';
import moment from 'moment';
import { AmlAccountAlerts } from 'common/Constants.js';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
const history = require('history').createBrowserHistory;

Chart.defaults.scale.gridLines.drawOnChartArea = false;
Chart.plugins.register(ChartDataLabels);

/*
 *
 *
 *
 * CONSTANTS
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
const DO_INIT_LOAD = process.env
  .REACT_APP_DEBUG_NO_LOAD_AML_DASHBOARD_ON_STARTUP
  ? false
  : true;
const SCENARIO_RESOLVER_MAP = new Map();
for (let [scenario, resolver] of [
  ['Rapid Movement', 'rapidMovement'],
  ['Accumulation', 'accumulation'],
  ['Concentration', 'concentration'],
  ['High-Risk Geo', 'highRiskGeo'],
  ['High-Risk Entity', 'highRiskEntity'],
  ['Unknown Owner', 'unknownOwner'],
  ['Cash Intensive Business', 'cashIntensiveBusiness']
]) {
  SCENARIO_RESOLVER_MAP.set(scenario, resolver);
}
const SCENARIO_TITLES = Array.from(SCENARIO_RESOLVER_MAP.keys());
const MAX_SCEN_TITLE_LINES = Math.max(
  ...SCENARIO_TITLES.map(s => s.split(' ').length)
);
const STEP_FUNCTION_MAX_INTERVAL = 50;
const X_AXIS = ['High Risk', 'Moderate Risk'];
// keep REGIONS in sync to REGIONS in dashboard api
const REGIONS = [
  'New England',
  'Mid-Atlantic',
  'East North Central',
  'West North Central',
  'South Atlantic',
  'East South Central',
  'West South Central',
  'Mountain',
  'Pacific'
];
const NUM_REGION_COLUMNS = 3;
const DEFAULT_MIN_DATE = '2018-12-31';
const DEFAULT_MAX_DATE = '2020-06-01';
const DEFAULT_START_DATE = '2019-12-01';
const DEFAULT_END_DATE = '2019-12-31';

const ALERT_VOLUME_BY_SCENARIO_HEADERS = [
  'Scenario Type',
  'False Positive',
  'True Positive'
];
const ACCOUNT_ALERTS_HEADERS = ['Customer ID', 'Customer Name', 'Alert Status'];
const HIGH_RISK_COLOR = '#32CD32';
const MODERATE_RISK_COLOR = '#98e698';
const VOLUME_ALERTS_BY_DATE_COLORS = '#ab2136';
const BASE_SCENARIO_CHART_OPTIONS = {
  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          min: 0,
          display: false
        }
      }
    ]
  },
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  title: {},
  tooltips: { enabled: false },
  plugins: {
    datalabels: {
      labels: {
        title: {
          font: {
            weight: 'bold'
          }
        }
      }
    }
  }
};

const useStyles = makeStyles(() => ({
  scenByTransRoot: {
    height: 800
  },
  scenarioBarChartCanvas: {
    height: 83
  },
  checkboxLabel: {
    fontWeight: 'bold'
  },
  header: {
    fontWeight: 'bold'
  },
  dashboardTitle: {
    fontWeight: 'bold',
    padding: '20px',
    textAlign: 'center',
    borderRadius: '6px'
  },
  title: {
    fontWeight: 'bold'
  },
  circularProgressParent: {
    position: 'relative'
  },
  circularProgressOverlayLeftBc: {
    position: 'absolute',
    left: '58%',
    top: '0px',
    zIndex: 2
  },
  circularProgressOverlayRightBc: {
    position: 'absolute',
    left: '46%',
    top: '0px',
    zIndex: 2
  },
  circularProgressOverlayTable: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    zIndex: 2
  },
  circularProgressOverlayVolByDate: {
    position: 'absolute',
    left: '50%',
    bottom: '40%',
    zIndex: 2
  }
}));

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: '#012874',
    color: theme.palette.common.white,
    fontSize: 14
  },
  body: {
    fontSize: 14,
    height: 15
  }
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default
    }
  }
}))(TableRow);

// functional chart options for conditionally displaying value labels
const ANCHOR_FUNCTION = function(context) {
  // if value is less than 50% of the max, display label on top of bar, else center it
  const chartMax = context.chart.options.scales.yAxes[0].ticks.max;
  var value = context.dataset.data[context.dataIndex];
  return value < chartMax / 2 ? 'end' : 'center';
};
const ALIGN_FUNCTION = function(context) {
  // if value is less than 50% of the max, display label on top of bar, else center it
  const chartMax = context.chart.options.scales.yAxes[0].ticks.max;
  var value = context.dataset.data[context.dataIndex];
  return value < chartMax / 2 ? 'top' : 'center';
};

/*
 *
 *
 *
 * DASHBOARD COMPONENT
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
export default function AMLDashboard(props) {
  const { value, handleChange, _, TabPanel, setMode } = props;

  //
  //
  //
  // SETUP HOOKS & STATE
  const [doInitLoad, setDoInitLoad] = React.useState(DO_INIT_LOAD);

  // scenario data
  const [rapidMovement, setRapidMovement] = React.useState({});
  const [accumulation, setAccumulation] = React.useState({});
  const [concentration, setConcentration] = React.useState({});
  const [highRiskGeo, setHighRiskGeo] = React.useState({});
  const [highRiskEntity, setHighRiskEntity] = React.useState({});
  const [unknownOwner, setUnknownOwner] = React.useState({});
  const [cashIntensiveBusiness, setCashIntensiveBusiness] = React.useState({});

  // load indicators
  const [rapidMovementLoading, setRapidMovementLoading] = React.useState(false);
  const [accumulationLoading, setAccumulationLoading] = React.useState(false);
  const [concentrationLoading, setConcentrationLoading] = React.useState(false);
  const [highRiskGeoLoading, setHighRiskGeoLoading] = React.useState(false);
  const [highRiskEntityLoading, setHighRiskEntityLoading] = React.useState(
    false
  );
  const [unknownOwnerLoading, setUnknownOwnerLoading] = React.useState(false);
  const [
    cashIntensiveBusinessLoading,
    setCashIntensiveBusinessLoading
  ] = React.useState(false);
  const [
    alertVolumePositivesLoading,
    setAlertVolumePositivesLoading
  ] = React.useState(false);
  const [
    alertVolumeByDateLoading,
    setAlertVolumeByDateLoading
  ] = React.useState(false);
  const resolverToStateMap = {
    rapidMovement: [
      rapidMovement,
      setRapidMovement,
      rapidMovementLoading,
      setRapidMovementLoading
    ],
    accumulation: [
      accumulation,
      setAccumulation,
      accumulationLoading,
      setAccumulationLoading
    ],
    concentration: [
      concentration,
      setConcentration,
      concentrationLoading,
      setConcentrationLoading
    ],
    highRiskGeo: [
      highRiskGeo,
      setHighRiskGeo,
      highRiskGeoLoading,
      setHighRiskGeoLoading
    ],
    highRiskEntity: [
      highRiskEntity,
      setHighRiskEntity,
      highRiskEntityLoading,
      setHighRiskEntityLoading
    ],
    unknownOwner: [
      unknownOwner,
      setUnknownOwner,
      unknownOwnerLoading,
      setUnknownOwnerLoading
    ],
    cashIntensiveBusiness: [
      cashIntensiveBusiness,
      setCashIntensiveBusiness,
      cashIntensiveBusinessLoading,
      setCashIntensiveBusinessLoading
    ]
  };

  const [startDate, setStartDate] = React.useState(
    new Date(DEFAULT_START_DATE)
  );
  const [endDate, setEndDate] = React.useState(new Date(DEFAULT_END_DATE));

  const initRegionState = new Map();
  for (let r of REGIONS) initRegionState.set(r, true); // check all regions on default
  const [regionState, setRegionState] = React.useState(initRegionState);

  const [alertVolumeByDate, setAlertVolumeByDate] = React.useState([]);
  const classes = useStyles();

  React.useEffect(() => {
    if (doInitLoad) {
      setDoInitLoad(false);
      fetchData();
    }
  });

  // SET UP REFS - "instance variables" to encapsulate chart data & etc to render outside of state
  const scenarioBarChartsDataRef = React.useRef();
  const fakeAlertVolumeByDateDataRef = React.useRef();
  const fakeAlertVolumePositivesToRenderRef = React.useRef();

  // SCENARIO BAR CHARTS DATA REF
  if (!scenarioBarChartsDataRef.current) {
    // initializing on first render
    scenarioBarChartsDataRef.current = {};
    const bcds = [];
    for (let scenarioTitle of SCENARIO_TITLES) {
      bcds.push({
        title: scenarioTitle,
        resolverName: SCENARIO_RESOLVER_MAP.get(scenarioTitle),
        canvasRefConsumer: createCanvasRef(),
        canvasRefCorporate: createCanvasRef(),
        charts: null
      });
    }
    scenarioBarChartsDataRef.current.data = bcds;
  }

  // PUT CURRENT STATE VARIABLES INTO IT
  for (let bcd of scenarioBarChartsDataRef.current.data) {
    const [
      _,
      setScenarioData,
      scenarioLoading,
      setScenarioLoading
    ] = resolverToStateMap[bcd.resolverName];
    bcd.setState = setScenarioData;
    bcd.loading = scenarioLoading;
    bcd.setLoading = setScenarioLoading;
  }

  // FAKE ALERT VOLUME POSITIVES REF INITIALIZE
  if (!fakeAlertVolumePositivesToRenderRef.current) {
    fakeAlertVolumePositivesToRenderRef.current = SCENARIO_TITLES.map(s => {
      return { scenario: s };
    });
  }

  // FAKE VOLUME BY DATE REF DATA REF
  if (!fakeAlertVolumeByDateDataRef.current) {
    fakeAlertVolumeByDateDataRef.current = {
      canvasRef: createCanvasRef()
    };
  }
  fakeAlertVolumeByDateDataRef.current.setState = setAlertVolumeByDate;
  fakeAlertVolumeByDateDataRef.current.data = alertVolumeByDate;

  // TODO REMOVE ONCE FALSE/TRUE POSITIVE NUMBERS ARE CORRECTLY HANDLED
  // this is a hack for generating fake false/true positive numbers by
  // basing them off of corresponding scenario data once loaded in
  // * only when loading indicator is up should we run logic to update fake data
  if (alertVolumePositivesLoading) {
    for (let d of fakeAlertVolumePositivesToRenderRef.current) {
      const scenarioData =
        resolverToStateMap[SCENARIO_RESOLVER_MAP.get(d.scenario)][0];
      if (!('falsePositives' in d) && Object.keys(scenarioData).length) {
        // if table row is empty AND corresponding scenario data from bar charts has come back
        const numTotalAlerts =
          scenarioData.highConsumer +
          scenarioData.highCorporate +
          scenarioData.moderateConsumer +
          scenarioData.moderateCorporate;
        const maxFalsePositives = 5;
        if (numTotalAlerts > maxFalsePositives * 2) {
          d.falsePositives = Math.floor(Math.random() * maxFalsePositives); // no more than 4 false positives
          d.truePositives = numTotalAlerts - d.falsePositives;
        } else {
          d.falsePositives = 0;
          d.truePositives = numTotalAlerts;
        }
      }
    }

    let doneLoading = true;
    for (let d of fakeAlertVolumePositivesToRenderRef.current) {
      if (!('falsePositives' in d)) {
        doneLoading = false;
        break;
      }
    }
    if (doneLoading) setAlertVolumePositivesLoading(false);
  }

  // TODO REMOVE ONCE NUMBERS ARE CORRECTLY HANDLED
  // if loading, check if it's done loading
  if (alertVolumeByDateLoading) {
    let avdDoneLoading = true;
    for (let k in resolverToStateMap) {
      if (!Object.keys(resolverToStateMap[k][0]).length) {
        avdDoneLoading = false;
        break;
      }
    }

    // when done loading, generate fake data set based on total number of alerts that came back
    if (avdDoneLoading) {
      // determine months to display
      let startDateMom = moment(startDate);
      let endDateMom = moment(endDate);
      var alertVolByDateData = [];
      while (startDateMom.isSameOrBefore(endDateMom)) {
        alertVolByDateData.push(startDateMom.clone());
        startDateMom.add(1, 'month');
      }
      if (
        alertVolByDateData[alertVolByDateData.length - 1].month() !==
        endDateMom.month()
      )
        alertVolByDateData.push(endDateMom.clone());

      // x-axis formatting for months
      const avdChart = fakeAlertVolumeByDateDataRef.current.chart;
      avdChart.data.labels = alertVolByDateData.map(d => d.format('MM/YYYY'));

      // count total alerts that came back
      let totalAlerts = 0;
      for (let [resolverName, states] of Object.entries(resolverToStateMap)) {
        const data = states[0];
        for (let [key, count] of Object.entries(data)) totalAlerts += count;
      }

      // divide total alerts randomly to display for each month by choosing
      // random breakpoints from 0 to totalAlerts
      let randomBreakpoints = [];
      for (let _ in Array.from(Array(avdChart.data.labels.length - 1))) {
        const rand = Math.floor(Math.random() * totalAlerts);
        if (!randomBreakpoints.includes(rand)) randomBreakpoints.push(rand);
      }
      randomBreakpoints = randomBreakpoints.sort((a, b) => a - b);
      const randomizedDataSet = [];
      let lastPoint = 0;
      for (let rb of randomBreakpoints) {
        randomizedDataSet.push(rb - lastPoint);
        lastPoint = rb;
      }
      randomizedDataSet.push(totalAlerts - lastPoint);
      console.assert(
        randomizedDataSet.reduce((sum, x) => sum + x) === totalAlerts
      );

      // configure and update chart
      avdChart.options.scales.yAxes[0].ticks.max = Math.max(
        ...randomizedDataSet
      );
      for (let dataset of avdChart.data.datasets)
        dataset.data = randomizedDataSet;
      avdChart.update();
      // alertVolumeByDateDataRef.current.setState(avbdNewData);
      setAlertVolumeByDateLoading(false);
    }
  }

  ///////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////

  //
  //
  //
  // FUNCTIONS
  const handleStartDateChange = startDate => {
    setStartDate(startDate);
  };
  const handleEndDateChange = endDate => {
    setEndDate(endDate);
  };
  const maxStepFunction = x => {
    return (
      Math.ceil(x / STEP_FUNCTION_MAX_INTERVAL) * STEP_FUNCTION_MAX_INTERVAL
    );
  };

  const fetchData = () => {
    //
    // CLEARING CHART DATA
    for (let bcd of scenarioBarChartsDataRef.current.data) {
      for (let dataset of bcd.charts.consumer.data.datasets)
        dataset.data = null;
      bcd.charts.consumer.update();
      for (let dataset of bcd.charts.corporate.data.datasets)
        dataset.data = null;
      bcd.charts.corporate.update();

      bcd.setState({});
    }

    fakeAlertVolumePositivesToRenderRef.current = null;

    for (let dataset of fakeAlertVolumeByDateDataRef.current.chart.data
      .datasets)
      dataset.data = null;
    fakeAlertVolumeByDateDataRef.current.chart.update();

    //
    // REQUESTING CHART DATA

    // format dates
    const startDateStr = moment(startDate).format('YYYY-MM-DD');
    const endDateStr = moment(endDate).format('YYYY-MM-DD');

    // gather checked regions for gql arguments
    const checkedRegions = [];
    for (let [r, checked] of regionState) if (checked) checkedRegions.push(r);

    // scenario charts
    for (let bcd of scenarioBarChartsDataRef.current.data) {
      const qry = `
        query {
          ${
            bcd.resolverName
          }(startDate: "${startDateStr}", endDate: "${endDateStr}", regions: ${JSON.stringify(
        checkedRegions
      )}) {
            highConsumer, highCorporate, moderateConsumer, moderateCorporate
          }
        }
      `;

      // querying scenario data then update charts
      bcd.setLoading(true);
      axios
        .post(process.env.REACT_APP_DASHBOARD_URI, { query: qry })
        .then(result => {
          const newData = result.data.data[bcd.resolverName];

          for (let dataset of bcd.charts.consumer.data.datasets)
            dataset.data = [newData.highConsumer, newData.moderateConsumer];
          bcd.charts.consumer.options.scales.yAxes[0].ticks.max = maxStepFunction(
            Math.max(newData.highConsumer, newData.moderateConsumer)
          );
          bcd.charts.consumer.update();

          for (let dataset of bcd.charts.corporate.data.datasets)
            dataset.data = [newData.highCorporate, newData.moderateCorporate];
          bcd.charts.corporate.options.scales.yAxes[0].ticks.max = maxStepFunction(
            Math.max(newData.highCorporate, newData.moderateCorporate)
          );
          bcd.charts.corporate.update();

          bcd.setLoading(false);
          bcd.setState(newData);
        })
        .catch(error => {
          console.log('caught error for scenario ' + bcd.resolverName, error);
        });
    }

    // alert volume positives
    setAlertVolumePositivesLoading(true);
    axios
      .post(process.env.REACT_APP_DASHBOARD_URI, {
        query: `
      query {
        alertVolumePositives(startDate: "${startDateStr}", endDate: "${startDateStr}", regions: ${JSON.stringify(
          checkedRegions
        )}) {
          scenario, truePositives, falsePositives
        }
      }`
      })
      .then(result => {
        // TODO use results once stub api is replaced with real query
        // alertVolumePositivesDataRef.current.setLoading(false);
        // [{"scenario":"Rapid Movement","truePositives":18,"falsePositives":2},
        //  {"scenario":"Accumulation","truePositives":11,"falsePositives":0},   {"scenario":"Concentration","truePositives":12,"falsePositives":3},{"scenario":"High-Risk Geo","truePositives":24,"falsePositives":3},{"scenario":"High-Risk Entity","truePositives":12,"falsePositives":0},{"scenario":"Unknown Owner","truePositives":32,"falsePositives":2},{"scenario":"Cash Intensive Business","truePositives":32,"falsePositives":2}]
        // alertVolumePositivesDataRef.current.setState(result.data.data.alertVolumePositives);
      });

    setAlertVolumeByDateLoading(true);
    // TODO alert volume by date querying logic here
  };

  //
  //
  //
  // RENDER
  return (
    <div>
      <Box
        borderColor="text.disabled"
        border={1}
        className={classes.dashboardTitle}>
        AML Alert Dashboard
      </Box>
      {/* <Box color="primary.main" border={1} className={classes.dashboardTitle}>AML Alert Dashboard</Box> */}

      {/* DATE/SUBMIT ROW */}
      <Grid container>
        {/* date widgets */}
        <Grid item xs={3} />
        <Grid container item xs={7}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container>
              <Grid item xs={4}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="MM/dd/yyyy"
                  margin="normal"
                  label="From"
                  minDate={new Date(DEFAULT_MIN_DATE)}
                  maxDate={new Date(DEFAULT_MAX_DATE)}
                  value={startDate}
                  onChange={handleStartDateChange}
                  KeyboardButtonProps={{ 'aria-label': 'change date' }}
                />
              </Grid>

              <Grid item xs={4}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="MM/dd/yyyy"
                  margin="normal"
                  label="To"
                  minDate={new Date(DEFAULT_MIN_DATE)}
                  maxDate={new Date(DEFAULT_MAX_DATE)}
                  value={endDate}
                  onChange={handleEndDateChange}
                  KeyboardButtonProps={{ 'aria-label': 'change date' }}
                />
              </Grid>
              <Grid item xs={2}>
                <CardActions
                  className={classes.actions}
                  style={{ paddingTop: '25px' }}>
                  <Button
                    position="right"
                    color="secondary"
                    size="medium"
                    variant="outlined"
                    style={{ fontWeight: '500', border: '1px solid' }}
                    onClick={() => {
                      moment(endDate).isBefore(moment(startDate))
                        ? alert('To date must be after From date!')
                        : fetchData();
                    }}>
                    Submit
                  </Button>
                </CardActions>
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Grid>
      </Grid>

      <Grid container>
        {/* SCENARIO BY ACCOUNT HOLDER TYPE */}
        <Grid container item xs={6}>
          <Card className={classes.title}>
            <CardHeader
              classes={{ title: classes.title }}
              title={'Scenario by Customer Type'}
            />
            <Divider />
            <ScenarioBarCharts
              scenarioBarChartsDataRef={scenarioBarChartsDataRef}
            />
          </Card>
        </Grid>

        {/* UPPER RIGHT - Alert Volume by Scenario table */}
        <Grid container item xs={6}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                classes={{ title: classes.title }}
                title={'Account Alerts'}
              />
              <Divider />
              <CardContent className={classes.circularProgressParent}>
                <TableContainer>
                  <Table>
                    {/* <TableHead>
                      <StyledTableRow>
                        {ALERT_VOLUME_BY_SCENARIO_HEADERS.map((h, i) => (
                          <StyledTableCell key={i}>{h}</StyledTableCell>
                        ))}
                      </StyledTableRow>
                    </TableHead> */}
                    <TableHead>
                      <StyledTableRow>
                        {ACCOUNT_ALERTS_HEADERS.map((h, i) => (
                          <StyledTableCell key={i}>{h}</StyledTableCell>
                        ))}
                      </StyledTableRow>
                    </TableHead>

                    <TableBody>
                      {Object.entries(AmlAccountAlerts).map((e, i) => (
                        <StyledTableRow key={i}>
                          <StyledTableCell>
                            <Link
                              onClick={() => {
                                setMode(true);

                                localStorage.setItem('mode', true);
                              }}
                              to={{
                                pathname: '/solution/Shared PII',
                                info: {
                                  customerID: AmlAccountAlerts[i].id,
                                  customerName: AmlAccountAlerts[i].name
                                }
                              }}>
                              {AmlAccountAlerts[i].id}
                            </Link>
                          </StyledTableCell>

                          <StyledTableCell>
                            {AmlAccountAlerts[i].name}
                          </StyledTableCell>
                          <StyledTableCell>
                            <FiberManualRecordIcon
                              style={{ color: AmlAccountAlerts[i].color }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}

                      {/* {fakeAlertVolumePositivesToRenderRef.current.map(
                        (d, i) => (
                          <StyledTableRow key={i}>
                            <StyledTableCell>{d.scenario}</StyledTableCell>
                            <StyledTableCell>
                              {'falsePositives' in d ? d.falsePositives : ''}
                            </StyledTableCell>
                            <StyledTableCell>
                              {'truePositives' in d ? d.truePositives : ''}
                            </StyledTableCell>
                          </StyledTableRow>
                        )
                      )} */}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* {alertVolumePositivesLoading ? (
                  <CircularProgress
                    color="secondary"
                    className={classes.circularProgressOverlayTable}
                  />
                ) : (
                  <div />
                )} */}
              </CardContent>
            </Card>

            {/* REGIONS CHECKBOXES */}
            <RegionCheckboxes
              classes={classes}
              regionState={regionState}
              setRegionState={setRegionState}
            />
          </Grid>
        </Grid>

        {/* BOTTOM CENTER */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              classes={{ title: classes.title }}
              title={'Volume of Alerts by Date'}
            />
            <Divider />
            <CardContent className={classes.circularProgressParent}>
              <AlertVolumeByDateGraph
                alertVolumeByDateDataRef={fakeAlertVolumeByDateDataRef}
              />
              {alertVolumeByDateLoading ? (
                <CircularProgress
                  color="secondary"
                  className={classes.circularProgressOverlayVolByDate}
                />
              ) : (
                <div />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}

AMLDashboard.propTypes = {
  value: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
  a11yProps: PropTypes.func.isRequired,
  TabPanel: PropTypes.func.isRequired
};

function createCanvasRef() {
  return React.createRef();
}

/*
 *
 *
 *
 * SCENARIO BAR CHARTS COMPONENT
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
function ScenarioBarCharts(props) {
  const { scenarioBarChartsDataRef } = props;
  const classes = useStyles();
  React.useEffect(function() {
    initScenarioBarChartsOnCanvas(scenarioBarChartsDataRef);
  });

  return (
    <CardContent>
      {/* main wrapper */}
      <Grid container>
        {/* titles */}
        <Grid container>
          {/* TODO align with padding instead of stock */}
          <Grid item xs={3} />
          <Grid item xs={5}>
            Consumer
          </Grid>
          <Grid item xs={4}>
            Corporate
          </Grid>
        </Grid>

        {/* charts, map on each row */}
        <Grid container>
          {props.scenarioBarChartsDataRef.current.data.map((bcd, i) => (
            <Grid container key={i}>
              <Grid item xs={6} className={classes.circularProgressParent}>
                <canvas
                  ref={bcd.canvasRefConsumer}
                  className={classes.scenarioBarChartCanvas}
                />
                {bcd.loading ? (
                  <CircularProgress
                    color="secondary"
                    className={classes.circularProgressOverlayLeftBc}
                  />
                ) : (
                  <div />
                )}
              </Grid>
              <Grid item xs={6} className={classes.circularProgressParent}>
                <canvas
                  ref={bcd.canvasRefCorporate}
                  className={classes.scenarioBarChartCanvas}
                />
                {bcd.loading ? (
                  <CircularProgress
                    color="secondary"
                    className={classes.circularProgressOverlayRightBc}
                  />
                ) : (
                  <div />
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </CardContent>
  );
}

ScenarioBarCharts.propTypes = {
  scenarioBarChartsDataRef: PropTypes.object.isRequired
};

function initScenarioBarChartsOnCanvas(scenarioBarChartsDataRef) {
  const bcdData = scenarioBarChartsDataRef.current.data;

  // for each iteration, initialize row of charts
  for (let i = 0; i < bcdData.length; i++) {
    const bcd = bcdData[i];
    if (bcd.charts !== null) continue;

    bcd.charts = {};

    // (for chart label formatting) split title into list of strings, each item is a line
    let titleTextTokens = bcd.title.split(' ');
    if (titleTextTokens.length < MAX_SCEN_TITLE_LINES) {
      let diff = MAX_SCEN_TITLE_LINES - titleTextTokens.length;
      for (let i = 0; i < diff; i++) titleTextTokens.unshift('');
    }

    // set up base chart configurations
    const consConfig = {
      type: 'bar',
      data: {
        labels: i === bcdData.length - 1 ? X_AXIS : ['', ''], // display if last two graphs
        datasets: [
          {
            data: null,
            backgroundColor: [HIGH_RISK_COLOR, MODERATE_RISK_COLOR]
          }
        ]
      },
      options: {
        ...BASE_SCENARIO_CHART_OPTIONS
      }
    };
    const corpConfig = JSON.parse(JSON.stringify(consConfig)); // clone

    // initial creation of Charts
    consConfig.options.title.display = true;
    consConfig.options.title.text = titleTextTokens;
    consConfig.options.title.position = 'left';
    consConfig.options.title.fontSize = 13;
    consConfig.options.title.display = true;
    consConfig.options.plugins.datalabels.anchor = ANCHOR_FUNCTION;
    consConfig.options.plugins.datalabels.align = ALIGN_FUNCTION;

    bcd.charts.consumer = new Chart(
      bcd.canvasRefConsumer.current.getContext('2d'),
      consConfig
    );
    corpConfig.options.title.display = false;
    corpConfig.options.plugins.datalabels.anchor = ANCHOR_FUNCTION;
    corpConfig.options.plugins.datalabels.align = ALIGN_FUNCTION;
    bcd.charts.corporate = new Chart(
      bcd.canvasRefCorporate.current.getContext('2d'),
      corpConfig
    );
  }
}

/*
 *
 *
 *
 * REGION CHECKBOXES COMPONENT
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
function RegionCheckboxes(props) {
  const { classes, regionState, setRegionState } = props;
  const columnWidth = Math.floor(12 / NUM_REGION_COLUMNS); // TODO not robust

  const onRegionChecked = ev => {
    const newState = new Map(regionState);
    newState.set(ev.target.name, ev.target.checked);
    setRegionState(newState);
  };

  return (
    <Card>
      <CardHeader classes={{ title: classes.title }} title={'Regions'} />
      <Divider />
      <CardContent>
        <Grid container>
          {Array.from(Array(NUM_REGION_COLUMNS)).map((_, i) => {
            const start = i * 3;
            const end = start + 3;
            return (
              <Grid item xs={columnWidth} key={i}>
                <FormControl component="fieldset">
                  <FormGroup>
                    {REGIONS.slice(start, end).map((k, i) => (
                      <FormControlLabel
                        key={i}
                        control={
                          <Checkbox
                            checked={regionState.get(k)}
                            name={k}
                            onChange={ev => onRegionChecked(ev)}
                          />
                        }
                        label={k}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}

RegionCheckboxes.propTypes = {
  classes: PropTypes.object.isRequired,
  regionState: PropTypes.object.isRequired,
  setRegionState: PropTypes.func.isRequired
};

/*
 *
 *
 *
 * TODO ALERT VOLUME BRANCH COMPONENT
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
function AlertVolumeByDateGraph(props) {
  const { alertVolumeByDateDataRef } = props;
  const canvasRef = alertVolumeByDateDataRef.current.canvasRef;
  React.useEffect(() => {
    if (!alertVolumeByDateDataRef.current.chart) {
      alertVolumeByDateDataRef.current.chart = new Chart(
        canvasRef.current.getContext('2d'),
        {
          type: 'bar',
          data: {
            datasets: [
              {
                data: alertVolumeByDateDataRef.current.data,
                backgroundColor: VOLUME_ALERTS_BY_DATE_COLORS
              }
            ]
          },
          options: {
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    min: 0
                  }
                }
              ]
            },
            maintainAspectRatio: false,
            legend: {
              display: false
            },
            plugins: {
              datalabels: {
                anchor: ANCHOR_FUNCTION,
                align: ALIGN_FUNCTION,
                labels: {
                  title: {
                    font: {
                      weight: 'bold'
                    }
                  }
                }
              }
            }
          }
        }
      );
    }
  });

  return (
    <div>
      <canvas
        ref={canvasRef}
        // className={classes.scenByTransEach}
      />
    </div>
  );
}

AlertVolumeByDateGraph.propTypes = {
  alertVolumeByDateDataRef: PropTypes.object.isRequired
};
